import {
  citationNamesMatchLoosely,
  citationPageNumbers,
  PAGE_CITATION_LIST_PATTERN,
} from '@/components/common/pageCitation';
import { KNOWLEDGE_BASE_DOCUMENT_PARAMS, ROUTES } from '@/constant/routes';
import type { ChatMessage, LibraryReference } from '@/types/chat';

import { readToolPayload } from './toolOutput';

export type { LibraryReference };

/**
 * Library Documents named by Connie's two Library tools (FSI, Oct 2026).
 *
 * `search_library` → `{status, documents: [{documentId, name, pages}]}`
 * `read_library_pages` → `{status, documentId, documentName, pagesOpened, pages}`
 *
 * `documentId` is the Xano `documents.id` the Knowledge Base page uses. Page
 * text is never read here: chat state cuts the payload to 8,000 characters,
 * and `pagesOpened` comes before `pages`, so it survives the cut.
 */
export type LibraryDocumentRef = {
  documentId: string;
  name?: string;
  /** Pages `read_library_pages` opened. A search hit is not a citation. */
  pagesRead: number[];
};

export const SEARCH_LIBRARY_TOOL = 'search_library';
export const READ_LIBRARY_PAGES_TOOL = 'read_library_pages';

const DOCUMENT_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readName = (value: unknown) =>
  typeof value === 'string' && value.trim()
    ? value.replace(/\s+/g, ' ').trim()
    : undefined;

const readPages = (value: unknown): number[] =>
  Array.isArray(value)
    ? value.filter((page): page is number => Number.isInteger(page) && page > 0)
    : [];

export const isLibraryTool = (toolName: string) =>
  toolName === SEARCH_LIBRARY_TOOL || toolName === READ_LIBRARY_PAGES_TOOL;

/**
 * The document's Knowledge Base page with its details and a new chat open,
 * at `page` when Connie cited or read one.
 */
export const libraryDocumentHref = (documentId: string, page?: number) => {
  const params = new URLSearchParams();
  if (page && Number.isInteger(page) && page > 0) {
    params.set(KNOWLEDGE_BASE_DOCUMENT_PARAMS.page, String(page));
  }
  params.set(KNOWLEDGE_BASE_DOCUMENT_PARAMS.newChat, 'true');
  return ROUTES.KNOWLEDGE_BASE_DOCUMENT({
    pageId: encodeURIComponent(documentId),
    searchParams: params.toString(),
    details: true,
    chat: true,
  });
};

export const libraryDocumentLabel = (ref: Pick<LibraryDocumentRef, 'name'>) =>
  ref.name ?? 'Library Document';

export function readLibraryDocuments(
  toolName: string,
  output: unknown
): LibraryDocumentRef[] {
  if (!isLibraryTool(toolName)) return [];
  const record = asRecord(readToolPayload(output));
  if (!record) return [];

  if (toolName === READ_LIBRARY_PAGES_TOOL) {
    const documentId = record.documentId;
    if (typeof documentId !== 'string' || !DOCUMENT_ID.test(documentId)) {
      return [];
    }
    return [
      {
        documentId,
        name: readName(record.documentName),
        pagesRead: readPages(record.pagesOpened),
      },
    ];
  }

  if (!Array.isArray(record.documents)) return [];
  const found: LibraryDocumentRef[] = [];
  for (const item of record.documents) {
    const document = asRecord(item);
    const documentId = document?.documentId;
    if (typeof documentId !== 'string' || !DOCUMENT_ID.test(documentId)) {
      continue;
    }
    if (found.some(current => current.documentId === documentId)) continue;
    found.push({ documentId, name: readName(document?.name), pagesRead: [] });
  }
  return found;
}

/** `p. 3`, `p. 3–5`, or `p. 3, 7` for pages that do not run on. */
export function formatPageRange(pages: number[]): string {
  const sorted = [...new Set(pages)].sort((a, b) => a - b);
  if (!sorted.length) return '';
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (sorted.length === 1) return `p. ${first}`;
  if (last - first === sorted.length - 1) return `p. ${first}–${last}`;
  return `p. ${sorted.join(', ')}`;
}

/** Pills for a Library tool row, or null for any other tool. */
export function libraryChipParts(
  toolName: string,
  output: unknown
): string[] | null {
  if (!isLibraryTool(toolName)) return null;
  const record = asRecord(readToolPayload(output));
  const status = typeof record?.status === 'string' ? record.status : '';

  if (toolName === SEARCH_LIBRARY_TOOL) {
    if (status === 'no_context') return ['No matching documents'];
    if (!Array.isArray(record?.documents)) return status ? [status] : null;
    const count = readLibraryDocuments(toolName, output).length;
    return [`${count} document${count === 1 ? '' : 's'}`];
  }

  const [document] = readLibraryDocuments(toolName, output);
  if (!document) return status ? [status] : null;
  if (!document.pagesRead.length) {
    return [libraryDocumentLabel(document), status || 'No pages opened'];
  }
  return [
    `${libraryDocumentLabel(document)} ${formatPageRange(document.pagesRead)}`,
  ];
}

/**
 * Page numbers the reply cites as `[<name>, p. <N>]` for this document name, matched
 * the way FSI and Ask Tia match them (`citationNamesMatch`).
 */
export function citedPages(content: string, name: string | undefined) {
  if (!name || !content) return [];
  const pages: number[] = [];
  for (const match of content.matchAll(PAGE_CITATION_LIST_PATTERN)) {
    if (!citationNamesMatchLoosely(match.groups?.name, name)) continue;
    for (const page of citationPageNumbers(match.groups?.pages ?? '')) {
      if (!pages.includes(page)) pages.push(page);
    }
  }
  return pages;
}

/**
 * One list of references with no document twice: `own` first, then `extra`
 * documents not already there. A follow-up turn cites documents an earlier turn
 * read, so the renderer matches against everything the session has touched.
 */
export function mergeReferences(
  own: readonly LibraryReference[],
  extra: readonly LibraryReference[]
): LibraryReference[] {
  const merged: LibraryReference[] = [...own];
  for (const ref of extra) {
    const current = merged.find(item => item.documentId === ref.documentId);
    if (!current) {
      merged.push(ref);
    } else if (ref.name && !current.name) {
      current.name = ref.name;
    }
  }
  return merged;
}

/**
 * The turn's Library references: the ones FSI sent on the done event or with the
 * session history, else the ones read from this turn's tool outputs.
 */
export function libraryReferencesFor(
  message: Pick<
    ChatMessage,
    'content' | 'toolCallOutputs' | 'libraryReferences'
  >
): LibraryReference[] {
  if (message.libraryReferences?.length) return message.libraryReferences;
  const references: LibraryReference[] = [];
  for (const output of message.toolCallOutputs ?? []) {
    for (const ref of readLibraryDocuments(output.toolName, output.output)) {
      const current = references.find(
        item => item.documentId === ref.documentId
      );
      if (current) {
        for (const page of ref.pagesRead) {
          if (!current.pagesRead.includes(page)) current.pagesRead.push(page);
        }
        if (ref.name && !current.name) current.name = ref.name;
        continue;
      }
      references.push({
        documentId: ref.documentId,
        name: ref.name ?? '',
        pagesRead: [...ref.pagesRead],
        cited: [],
      });
    }
  }
  for (const ref of references) {
    ref.pagesRead.sort((a, b) => a - b);
    ref.cited = citedPages(message.content, ref.name || undefined);
  }
  return references;
}

/**
 * The page a link opens at: the first page the reply cites, otherwise the
 * first page Connie read. The drawer shows the same page as "Library · p. N".
 */
export function libraryDocumentPage(
  ref: Pick<LibraryDocumentRef, 'name' | 'pagesRead'>,
  content = ''
): number | undefined {
  const cited = citedPages(content, ref.name);
  const pages = cited.length ? cited : ref.pagesRead;
  return pages.length ? Math.min(...pages) : undefined;
}
